// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

// Services
import { ChannelService } from '../services';
import { CoreService } from '../../../shared/services/core.service';
import { SpinnerService } from '../../../shared/components/spinner/services/spinner.service';

// Models
import { Channel } from '../models';
import { AppCleanup } from '../../../shared/models';

// Store
import { ChannelActions } from './channel.action';

// Enums
import { StatusType } from '../../../shared/enums';
import { patch, updateItem } from '@ngxs/store/operators';

export interface ChannelStateModel {
	items: Channel[];
	loaded: boolean;
}

const defaultState: ChannelStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'channel',
	defaults: defaultState,
})
@Injectable()
export class ChannelState extends AppCleanup {
	constructor(
		private channelService: ChannelService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
	}

	@Action(ChannelActions.GetAll)
	getAll(ctx: StateContext<ChannelStateModel>, action: ChannelActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.channelService
			.getChannels()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (channels) => {
					ctx.setState({
						items: channels,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(ChannelActions.SetAll)
	setAll(ctx: StateContext<ChannelStateModel>, action: ChannelActions.SetAll) {
		ctx.setState({
			items: action.channels,
			loaded: true,
		});
	}

	@Action(ChannelActions.Update)
	update(ctx: StateContext<ChannelStateModel>, action: ChannelActions.Update) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.channelService
			.updateChannel(action.data.channel)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (channel) => {
					const currentState = ctx.getState();
					const existingItem = currentState.items?.find((item) => item.id === channel.id);

					if (existingItem) {
						ctx.setState(
							patch({
								items: updateItem((channel) => channel.id === action.data.channel.id, action.data.channel),
							})
						);
					}

					ctx.dispatch(new ChannelActions.UpdateSuccess(channel));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new ChannelActions.UpdateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Selector()
	static isLoaded(state: ChannelStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static channels(state: ChannelStateModel): Channel[] {
		return state.items;
	}
}
